import { CollectionHandleKey } from 'config/collections/handles';
import { getAllHandles } from 'config/collections/utils/get-all-handles';
import { slugify } from 'utils/strings';

/**
 * Segmentation Ids for allergy
 */
export const AllergySegmentationIds = {
  GrainFree: 'grain-free',
  Beef: 'beef',
  Chicken: 'chicken',
  Duck: 'duck',
  Fish: 'fish',
  Lamb: 'lamb',
  Horse: 'horse',
  Goat: 'goat',
  SweetPotato: 'sweet-potato',
  Potato: 'potato',
} as const;

/**
 * Segmentation Ids for cat allergy
 */
export const CatAllergySegmentationIds = {
  /** Duck */
  Duck: 'duck',
  /** Poultry */
  Poultry: 'poultry',
  /** Deer */
  Deer: 'deer',
  /** Chicken */
  Chicken: 'chicken',
  /** Rabbit */
  Rabbit: 'rabbit',
  /** Veal */
  Veal: 'veal',
  /** Kangaroo */
  Kangaroo: 'kangaroo',
  /** Salmon */
  Salmon: 'salmon',
  /** Turkey */
  Turkey: 'turkey',
  /** Tuna */
  Tuna: 'tuna',
  /** Game */
  Game: 'game',
  /** Beef */
  Beef: 'beef',
  /** Pork */
  Pork: 'pork',
  /** Horse */
  Horse: 'horse',
} as const;

export type CatAllergySegmentationIds =
  (typeof CatAllergySegmentationIds)[keyof typeof CatAllergySegmentationIds];

const CatAllergySegmentationSet = new Set(Object.values(CatAllergySegmentationIds));

/**
 * Assert function for CatAllergySegmentationIds
 */
export function isCatAllergySegmentation(
  segmentation: SegmentationIds
): segmentation is CatAllergySegmentationIds {
  return CatAllergySegmentationSet.has(segmentation as unknown as any);
}

// create cat allergy segmentation ids

export type AllergySegmentationIds =
  (typeof AllergySegmentationIds)[keyof typeof AllergySegmentationIds];

/**
 * Combined type for all allergy segmentation IDs
 */
export type AllAllergySegmentationIds = AllergySegmentationIds | CatAllergySegmentationIds;

const AllAllergySegmentationSet = new Set([
  ...Object.values(AllergySegmentationIds),
  ...Object.values(CatAllergySegmentationIds),
]);

/**
 * Assert function for AllergySegmentationIds
 */
export function isAllergySegmentation(
  segmentation: SegmentationIds
): segmentation is AllAllergySegmentationIds {
  return AllAllergySegmentationSet.has(segmentation as unknown as any);
}

/**
 * Segmentation Ids for dog age segmentation
 */
export const DogAgeSegmentationIds = {
  /** Dog Puppy */
  Welpe: 'welpe',
  /** Dog Adult  */
  DogAdult: 'dog-adult',
  /** Dog Senior  */
  DogSenior: 'senior',
} as const;

export type DogAgeSegmentationIds =
  (typeof DogAgeSegmentationIds)[keyof typeof DogAgeSegmentationIds];

/**
 * Segmentation Ids for cat age segmentation
 */
export const CatAgeSegmentationIds = {
  /** Kitten (0-1 years old) */
  Kitten: 'kitten',
  /** Cat Adult  */
  CatAdult: 'adultcat',
  /** Senior (older than 10 years) */
  CatSenior: 'seniorcat',
} as const;

export type CatAgeSegmentationIds =
  (typeof CatAgeSegmentationIds)[keyof typeof CatAgeSegmentationIds];

const AgeSegmentationSet = new Set(
  [Object.values(DogAgeSegmentationIds), Object.values(CatAgeSegmentationIds)].flat()
);

export const AgeSegmentationIds = {
  ...DogAgeSegmentationIds,
  ...CatAgeSegmentationIds,
} as const;

export type AgeSegmentationIds = DogAgeSegmentationIds | CatAgeSegmentationIds;

/**
 * Assert function for AgeSegmentationIds
 */
export function isAgeSegmentation(
  segmentation: SegmentationIds
): segmentation is AgeSegmentationIds {
  return AgeSegmentationSet.has(segmentation as unknown as any);
}

export const NutritionSegmentationIds = {
  /** DigestionIssues */
  Digestion: 'digestion',
  /** intolerances or allergies */
  Intolerance: 'intolerance',
  /** Health prevention */
  Prevention: 'prevention',
  /** VeryPicky */
  PickyEater: 'pickyeater',
  /** Skin and coat problems */
  SkinFur: 'skin-fur',
  /** Overweight */
  Overweight: 'overweight',
  /** No help needed */
  NoHelpNeeded: 'no-help-needed',
  /** Needs guidance */
  Foodaid: 'foodaid',
} as const;

export type NutritionSegmentationIds =
  (typeof NutritionSegmentationIds)[keyof typeof NutritionSegmentationIds];

export const CatNutritionSegmentationIds = {
  /** Picky eater for cat */
  CatPickyEater: 'pickyeatercat',
  /** Digestion problem for cat */
  CatDigestion: 'digestioncat',
  /** Overweight cat */
  CatOverweight: 'overweightcat',
  /** Hairball help */
  HairballHelp: 'hairballcat',
  /** Immune system */
  ImmuneSystem: 'immunitycat',
  /** intolerances or allergies */
  IntoleranceCat: 'intolerancecat',
  /** Skin and coat problems */
  SkinFurCat: 'skinfurcat',
} as const;

export type CatNutritionSegmentationIds =
  (typeof CatNutritionSegmentationIds)[keyof typeof CatNutritionSegmentationIds];

export const BreedSegmentationIds = {
  /** Bulldog / Bulldogge */
  Bulldog: 'bulldog',
  /** Chihuahua   */
  Chihuahua: 'chihuahua',
  /** Dachshund / Dackel */
  Dachshund: 'dachshund',
  /** Havanese, Maltese / Havaner, Malteser */
  Havanese: 'havanese',
  /** Hütehund / Herding dog */
  Herdingdog: 'herdingdog',
  /** Mixed large  */
  MixedLarge: 'mixed-dog-large',
  /** Mixed small */
  MixedSmall: 'mixed-dog-small',
  /** Poodle / Pudel, Pudelmix */
  Poodle: 'poodle',
  /** Retriever */
  Retriever: 'retriever',
  /** ShepherdDog / Schäferhund */
  ShepherdDog: 'shepherd-dog',
  /** Spitz */
  Spitz: 'spitz',
  /** Terrier */
  Terrier: 'terrier',
  /** Other breed */
  OtherBreed: 'other-breed',
} as const;

export type BreedSegmentationIds = (typeof BreedSegmentationIds)[keyof typeof BreedSegmentationIds];

const BreedSegmentationSet = new Set(Object.values(BreedSegmentationIds));

/**
 * Assert function for BreedSegmentationIds
 */
export function isBreedSegmentation(
  segmentation: SegmentationIds
): segmentation is BreedSegmentationIds {
  return BreedSegmentationSet.has(segmentation as unknown as any);
}

/**
 * Protein tag values for taste preference for cat
 */
export const ProteinTagValues = {
  Duck: 'duck',
  Poultry: 'poultry',
  Deer: 'deer',
  Chicken: 'chicken',
  Rabbit: 'rabbit',
  Veal: 'veal',
  Kangaroo: 'kangaroo',
  Salmon: 'salmon',
  Turkey: 'turkey',
  Tuna: 'tuna',
  Game: 'game',
  Beef: 'beef',
  Pork: 'pork',
  Horse: 'horse',
} as const;

export type ProteinTagValues = (typeof ProteinTagValues)[keyof typeof ProteinTagValues];

export type DogSegmentationIds =
  BreedSegmentationIds | NutritionSegmentationIds | AllergySegmentationIds | DogAgeSegmentationIds;

export type CatSegmentationIds =
  | CatNutritionSegmentationIds
  | CatAllergySegmentationIds
  | CatAgeSegmentationIds
  | ProteinTagValues;

export type SegmentationIds = DogSegmentationIds | CatSegmentationIds;

const nutritionSegmentationSet = new Set(
  [Object.values(NutritionSegmentationIds), Object.values(CatNutritionSegmentationIds)].flat()
);

/**
 * Assert function for NutritionSegmentationIds
 */
export function isNutritionSegmentation(
  segmentation: SegmentationIds
): segmentation is NutritionSegmentationIds | CatNutritionSegmentationIds {
  return nutritionSegmentationSet.has(segmentation as unknown as any);
}

export const CatSegmentationIds = {
  ...CatAgeSegmentationIds,
  ...CatNutritionSegmentationIds,
  ...CatAllergySegmentationIds,
  ...ProteinTagValues,
} as const;

const catSegmentationSet = new Set(Object.values(CatSegmentationIds));

/**
 * Assert function for CatSegmentationIds
 */
export function isCatSegmentation(
  segmentation: SegmentationIds
): segmentation is CatSegmentationIds {
  return catSegmentationSet.has(segmentation as unknown as any);
}

export const DogSegmentationIds = {
  ...AllergySegmentationIds,
  ...DogAgeSegmentationIds,
  ...NutritionSegmentationIds,
  ...BreedSegmentationIds,
} as const;

export const SegmentationIds = {
  ...CatSegmentationIds,
  ...DogSegmentationIds,
} as const;

export interface Segmentation {
  id: SegmentationIds;
  redirectPages: ReadonlyArray<string>;
}

// The only non-collection page a segmentation points at.
export const FOOD_ADVISOR_PATH = '/futterberater';

export type SegmentationPage = CollectionHandleKey | typeof FOOD_ADVISOR_PATH;

type SegmentationPages = ReadonlyArray<{
  id: SegmentationIds;
  pages: ReadonlyArray<SegmentationPage>;
}>;

export const SEGMENTATION_PAGES: SegmentationPages = [
  {
    id: NutritionSegmentationIds.PickyEater,
    pages: [
      'dogsPickyEater',
      'dogsPickyEaterLp',
      'dogsPickyEaterLp1',
      'dogsPickyEaterLp2',
      'dogsPaidSocialTreatBox50A',
      'dogsPaidSocialTreatBox50B',
      'dogsPaidSocialTreatBox50ACh',
    ],
  },
  {
    id: NutritionSegmentationIds.Digestion,
    pages: [
      'dogsDigestion',
      'dogsDigestionLp',
      'dogsDigestionLp1',
      'dogsDigestionLp2',
      'dogsWetFoodClassicBundle40',
    ],
  },
  {
    id: NutritionSegmentationIds.Intolerance,
    pages: ['dogsIntolerances', 'dogsIntolerancesLp', 'dogsIntolerancesLp1'],
  },
  {
    id: NutritionSegmentationIds.SkinFur,
    pages: ['dogsSkinCoat', 'dogsSkinCoatLp'],
  },
  {
    id: NutritionSegmentationIds.Overweight,
    pages: ['dogsOverweight'],
  },
  {
    id: NutritionSegmentationIds.Prevention,
    pages: ['dogsHealthPrevention'],
  },
  {
    id: DogAgeSegmentationIds.Welpe,
    pages: ['dogsPuppyJunior', 'dogsPuppyJuniorLp1'],
  },
  {
    id: DogAgeSegmentationIds.DogAdult,
    pages: ['dogsAdult'],
  },
  {
    id: DogSegmentationIds.DogSenior,
    pages: ['dogsSenior'],
  },
  {
    id: NutritionSegmentationIds.Foodaid,
    pages: ['/futterberater'],
  },
  {
    id: BreedSegmentationIds.Bulldog,
    pages: ['dogsBulldog', 'dogsBulldogLp', 'dogsBulldogLp1', 'dogsBulldogLp2'],
  },
  {
    id: BreedSegmentationIds.Retriever,
    pages: ['dogsRetriever'],
  },
  {
    id: BreedSegmentationIds.Dachshund,
    pages: ['dogsDachshund', 'dogsDachshundLp', 'dogsDachshundLp1'],
  },
  {
    id: BreedSegmentationIds.Chihuahua,
    pages: ['dogsChihuahua', 'dogsChihuahuaLp', 'dogsChihuahuaLp1'],
  },
  {
    id: BreedSegmentationIds.Havanese,
    pages: ['dogsMaltese'],
  },
  {
    id: BreedSegmentationIds.Poodle,
    pages: ['dogsPoodle'],
  },
  {
    id: BreedSegmentationIds.ShepherdDog,
    pages: ['dogsShepherd'],
  },
  {
    id: BreedSegmentationIds.Spitz,
    pages: ['dogsSpitz'],
  },
  {
    id: BreedSegmentationIds.Terrier,
    pages: ['dogsTerrier', 'dogsTerrierLp'],
  },
  {
    id: BreedSegmentationIds.Herdingdog,
    pages: ['dogsHerdingDogs'],
  },
  {
    id: BreedSegmentationIds.MixedLarge,
    pages: ['dogsMixedBreed'],
  },
  {
    id: BreedSegmentationIds.MixedSmall,
    pages: ['dogsMixedBreed'],
  },
  {
    id: CatNutritionSegmentationIds.CatPickyEater,
    pages: ['catsPickyEater', 'catsPickyEaterLp'],
  },
  {
    id: CatNutritionSegmentationIds.CatDigestion,
    pages: ['catsDigestion'],
  },
  {
    id: CatNutritionSegmentationIds.CatOverweight,
    pages: ['catsOverweight'],
  },
  {
    id: CatAgeSegmentationIds.Kitten,
    pages: ['catsKitten'],
  },
  {
    id: CatAgeSegmentationIds.CatSenior,
    pages: ['catsSenior', 'catsSeniorLp'],
  },
  {
    id: CatAgeSegmentationIds.CatAdult,
    pages: ['catsAdult'],
  },
  {
    id: CatSegmentationIds.IntoleranceCat,
    pages: ['catsIntolerances'],
  },
  {
    id: CatSegmentationIds.SkinFurCat,
    pages: ['catsSkinCoat'],
  },
];

/**
 * Lookup table for url -> segmentation. It lists every region's path for a page, because the
 * region is unknown at module scope and a path identifies its segmentation without it.
 * To build a url for a known segmentation use getSegmentationPage, which takes a region.
 */
export const SegmentationData: { segmentation: ReadonlyArray<Segmentation> } = {
  segmentation: SEGMENTATION_PAGES.map(({ id, pages }) => ({
    id,
    redirectPages: pages.flatMap((page) => {
      if (page === FOOD_ADVISOR_PATH) {
        return [page];
      }
      return getAllHandles({ key: page }).map((handle) => `/collections/${handle}`);
    }),
  })),
};

/**
 * Map of filter tags before slugify to segmentation
 */
export const SEGMENTS_RAW_FILTER_MAP: {
  [key in string]: SegmentationIds | Array<SegmentationIds>;
} = {
  'Haut & Fellprobleme': SegmentationIds.SkinFur,
  'Sehr wählerisch': [SegmentationIds.PickyEater, SegmentationIds.CatPickyEater],
  'Adult (1-6 Jahre)': SegmentationIds.DogAdult,
  'Senior (8+ Jahre)': SegmentationIds.DogSenior,
  'Unverträglichkeiten / Allergien': SegmentationIds.Intolerance,
  adult: [SegmentationIds.DogAdult, SegmentationIds.CatAdult],
  Verdauungsprobleme: [SegmentationIds.Digestion, SegmentationIds.CatDigestion],
  Welpe: SegmentationIds.Welpe,
  übergewicht: SegmentationIds.Overweight,
  Gesundheitsprävention: SegmentationIds.Prevention,
  bulldog: SegmentationIds.Bulldog,
  chihuahua: SegmentationIds.Chihuahua,
  dackel: SegmentationIds.Dachshund,
  herdingdog: SegmentationIds.Herdingdog,
  maltese: SegmentationIds.Havanese,
  pudel: SegmentationIds.Poodle,
  retriever: SegmentationIds.Retriever,
  shepperd: SegmentationIds.ShepherdDog,
  spitz: SegmentationIds.Spitz,
  terrier: SegmentationIds.Terrier,
  [SegmentationIds.CatPickyEater]: SegmentationIds.CatPickyEater,
  [SegmentationIds.CatDigestion]: SegmentationIds.CatDigestion,
  [SegmentationIds.CatOverweight]: SegmentationIds.CatOverweight,
  [SegmentationIds.IntoleranceCat]: SegmentationIds.IntoleranceCat,
  [SegmentationIds.SkinFurCat]: SegmentationIds.SkinFurCat,
  [SegmentationIds.Kitten]: SegmentationIds.Kitten,
  [SegmentationIds.CatSenior]: SegmentationIds.CatSenior,
  [SegmentationIds.CatAdult]: SegmentationIds.CatAdult,
  [SegmentationIds.ImmuneSystem]: SegmentationIds.ImmuneSystem,
  [SegmentationIds.HairballHelp]: SegmentationIds.HairballHelp,
} as const;

/**
 * Segmentation shopify tags converted for usability for filter logics
 */
export const SEGMENTS_SLUGIFIED_FILTER_MAP = Object.fromEntries(
  Object.entries(SEGMENTS_RAW_FILTER_MAP).map((entry) => {
    return [slugify(entry[0]), entry[1]];
  })
);
